import { getAuthHeaders } from '@/lib/api-utils'

/**
 * Unified Issue Update Service.
 *
 * Single entry point for all standard issue field updates (status, priority,
 * assignee, notes, etc.) — routed via `/api/issues/:id`.
 */

export interface IssueUpdateData {
    issue?: {
        status_id?: number
        priority_id?: number
        assigned_to_id?: number | string
        tracker_id?: number
        subject?: string
        description?: string
        done_ratio?: number
        estimated_hours?: number | null
        start_date?: string | null
        due_date?: string | null
        fixed_version_id?: number | null
        parent_issue_id?: number | null
        category_id?: number | null
        notes?: string
        private_notes?: boolean
        [key: string]: any
    }
}

export interface IssueUpdateResult {
    success: boolean
    error?: string
    issueResult?: any
}

export async function updateIssue(
    issueId: string | number,
    data: IssueUpdateData,
): Promise<IssueUpdateResult> {
    const authHeaders = getAuthHeaders()
    const result: IssueUpdateResult = { success: true }

    if (data.issue && Object.keys(data.issue).length > 0) {
        try {
            const response = await fetch(`/api/issues/${issueId}`, {
                method: 'PUT',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ issue: data.issue }),
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                let errorMsg = errorData.error || 'Failed to update issue'

                if (response.status === 403) {
                    errorMsg = errorData.error || 'This status transition is not allowed by the workflow.'
                } else if (response.status === 422 && errorData.errors) {
                    const errors = Array.isArray(errorData.errors) ? errorData.errors : [errorData.errors]
                    errorMsg = errors.join(', ')
                }

                return { success: false, error: errorMsg }
            }

            result.issueResult = await response.json().catch(() => ({}))
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Network error updating issue',
            }
        }
    }

    return result
}

export interface IssueDeleteResult {
    success: boolean
    error?: string
}

/**
 * Delete an issue by ID. Hits DELETE /api/issues/:id which calls the
 * OrbitNest `issues` function with `action: 'delete'`.
 */
export async function deleteIssue(issueId: string | number): Promise<IssueDeleteResult> {
    const authHeaders = getAuthHeaders()
    try {
        const response = await fetch(`/api/issues/${issueId}`, {
            method: 'DELETE',
            headers: { ...authHeaders },
        })

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            let errorMsg: string = errorData.error || 'Failed to delete issue'
            if (response.status === 403) errorMsg = errorData.error || 'You do not have permission to delete this issue.'
            else if (response.status === 404) errorMsg = errorData.error || 'Issue not found.'
            return { success: false, error: errorMsg }
        }

        return { success: true }
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error deleting issue',
        }
    }
}
